/**
 * FirstRunTour (T10; Part-1 T3 spec): OWNERS only, exactly three steps,
 * skippable at any point, persisted SERVER-SIDE on the user record
 * (User.onboardingTourDismissedAt) — so it never re-arms on another device or
 * after a cache clear. Dependency-free: a centered modal sequence, no
 * spotlight library. All strings come from the T9 catalog.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import api from '../services/api';
import { t } from '../i18n';
import { BrandLogo } from '@/components/brand/BrandLogo';
import { TOUR_DISMISSED_EVENT } from './SetupChecklist/SetupChecklist';
import { useBusinessType } from '../config/businessPresets';
import { stepsFor } from './firstRunTourSteps';
import { ComingSoonBadge } from './ui/ComingSoonBadge';
import { isFeatureLive } from '../config/featureAvailability';

export function FirstRunTour() {
  const navigate = useNavigate();
  const { businessType } = useBusinessType();
  const STEPS = stepsFor(businessType);
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [me, profile] = await Promise.all([api.auth.me(), api.userProfile.get()]);
        if (cancelled) return;
        const workspaceId = api.getWorkspaceId();
        const role = me.data?.workspaces?.find((w) => w.id === workspaceId)?.role;
        const dismissed = profile.data?.onboardingTourDismissedAt;
        // Owners only; a tour a viewer can't act on is just noise.
        if (role === 'OWNER' && !dismissed) setVisible(true);
      } catch {
        // The tour is never worth an error state.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!visible) return null;

  const dismiss = (): void => {
    setVisible(false);
    // Fire-and-forget: losing the flag write only means seeing the tour once
    // more; blocking dismissal on the network would be worse.
    void api.userProfile.update({ tourDismissed: true }).catch(() => undefined);
    // Hand off to the setup checklist in this session (kind-alignment §4).
    window.dispatchEvent(new Event(TOUR_DISMISSED_EVENT));
  };

  const current = STEPS[step];
  const Icon = current.icon;
  const isLast = step === STEPS.length - 1;

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" onClick={dismiss} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('tour.title')}
        className="relative w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-200 p-6"
      >
        <button
          onClick={dismiss}
          aria-label={t('tour.skip')}
          className="absolute right-4 top-4 p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-50"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="mb-4 pr-8">
          <BrandLogo size="sm" />
        </div>
        <p className="text-xs font-medium text-gray-400 mb-4">
          {t('tour.stepCount', { current: step + 1, total: STEPS.length })}
        </p>

        <div className="w-11 h-11 rounded-xl bg-gray-900 text-white flex items-center justify-center mb-4">
          <Icon className="w-5 h-5" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900 mb-2">
          {t(current.title)}
          {current.feature !== undefined && !isFeatureLive(current.feature) && <ComingSoonBadge />}
        </h2>
        <p className="text-sm text-gray-600 leading-relaxed mb-6">{t(current.body)}</p>

        <div className="flex items-center justify-between">
          <button onClick={dismiss} className="text-sm text-gray-400 hover:text-gray-600">
            {t('tour.skip')}
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                dismiss();
                navigate(current.target);
              }}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg"
            >
              {t('tour.showMe')}
            </button>
            {step > 0 && (
              <button
                onClick={() => setStep((s) => s - 1)}
                className="px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 border border-gray-200 rounded-lg"
              >
                {t('tour.back')}
              </button>
            )}
            <button
              onClick={() => (isLast ? dismiss() : setStep((s) => s + 1))}
              className="px-4 py-2 text-sm font-semibold text-white bg-gray-900 hover:bg-gray-800 rounded-lg"
            >
              {isLast ? t('tour.finish') : t('tour.next')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
